import { sha1 } from 'object-hash';
import { BadRequestError } from '@cian/valuation-utils-component';

import { TThunkAction } from '../../types/redux';
import { EDealType } from '../../types/dealType';
import { TGetEstimationWebResponse } from './types';
import { prepareResponse } from './utils/prepareResponse';
import {
  fetchGetEstimationSaleWeb,
  TGetEstimationSaleWebResponse
} from '../../repositories/price-estimator/v1/get-estimation-sale-web';
import {
  fetchGetEstimationRentWeb,
  TGetEstimationRentWebResponse
} from '../../repositories/price-estimator/v1/get-estimation-rent-web';
import { getEstimationFailure, getEstimationSuccess, IGetEstimationSuccessPayload } from './actions';
import { selectApiParameters } from '../../selectors/estimation';
import { setPreciseFiltersState } from '../preciseFilters';
import { preparePreciseFilters } from '../preciseFilters/utils';
import { clearIsFromOfferCard } from '../page';

/**
 * Процесс в санке:
 * 1. Диспатч начала запроса за оценкой
 * 2. Выполнение запроса за оценкой с переданными параметрами
 * 3. Обработка ошибки: логирование, диспатч ошибки, возврат в случае ошибки
 * 4. Диспатч успешного ответа и его возврат
 * */
export const getEstimationByDealType =
  (dealType: EDealType): TThunkAction<Promise<IGetEstimationSuccessPayload | Error | BadRequestError>> =>
  async (dispatch, getState, context) => {
    const state = getState();
    const { estimation } = getState();
    const currentHash = estimation[dealType].filtersHash;
    const options = {
      parameters: selectApiParameters(state),
      httpApi: context.httpApi,
    };

    let response: TGetEstimationWebResponse;

    try {
      if (dealType === EDealType.Sale) {
        response = (await fetchGetEstimationSaleWeb(options)) as TGetEstimationSaleWebResponse;
      } else {
        response = (await fetchGetEstimationRentWeb(options)) as TGetEstimationRentWebResponse;
      }

      if (response.statusCode !== 200) {
        throw new BadRequestError({
          domain: 'getEstimationByDealType',
          message: response.response.message as string,
          details: { errors: JSON.stringify(response.response.errors) },
        });
      }
    } catch (e) {
      if (!(e instanceof BadRequestError)) {
        context.logger.error(e, {
          message: 'Ошибка при загрузке оценки',
          parameters: JSON.stringify(options.parameters),
        });
      } else {
        context.logger.debug('getEstimationByDealType: bad request', {
          message: e.message,
          domain: e.domain,
          parameters: JSON.stringify(options.parameters),
        });
      }

      dispatch(getEstimationFailure({ dealType }));

      return e;
    }

    const filtersHash = sha1(options.parameters);
    if (currentHash && currentHash !== filtersHash) {
      dispatch(clearIsFromOfferCard());
    }
    const data = prepareResponse(response, dealType);
    if (response.response.filtersFromDataPayment) {
      const preciseFilters = preparePreciseFilters(response.response.filtersFromDataPayment);

      dispatch(setPreciseFiltersState(preciseFilters));
    }

    const result = {
      dealType,
      data,
      filtersHash,
    };

    dispatch(getEstimationSuccess(result));

    return result;
  };
